'use client';

import { useA11yStatusMessage } from '@vcc-package/a11y-status-message';
import { useSharedComponentsTranslate } from '@vcc-www/shared-dictionaries';
import type { SiteSlug } from '@volvo-cars/market-sites';
import { useEffect } from 'react';
import { getDealerId } from '../../../utils/volvoTrackingHelpers';
import { useMarketConfig } from '../../providers/MarketConfigProvider';
import type { Retailer } from '../../types/retailer';
import { RetailerCard } from '../RetailerCard';
import { useRetailerCardInfo } from '../useRetailerCardInfo';
import { getRetailerLinks } from '../utils/getRetailerLinks';

type ContentProps = { retailer: Retailer; siteSlug: SiteSlug };

export const Content = ({ retailer, siteSlug }: ContentProps) => {
  const { firstDayOfWeek } = useMarketConfig();
  const { distance, addressHref, addressLines, name } =
    useRetailerCardInfo(retailer);
  const links = getRetailerLinks(retailer, siteSlug);
  const translate = useSharedComponentsTranslate();
  const { setStatusMessage } = useA11yStatusMessage();
  useEffect(() => {
    setStatusMessage(
      translate('RetailerSelector.retailerCard.selectedRetailerMessage', {
        name,
      }),
    );
  }, [name, setStatusMessage, translate]);

  return (
    <div data-testid="dealer:retailerContainer">
      <div className="flex-col gap-24">
        <RetailerCard.Header
          name={<h1 className="text-primary">{name}</h1>}
          distance={distance}
          id="expanded-card-title"
        />
        <RetailerCard.Address
          ariaLabelRetailerName={name}
          url={addressHref}
          addressLines={addressLines}
        />
        <RetailerCard.CapabilityPills capabilities={retailer.capabilities} />
        <RetailerCard.CapabilitySelection
          {...retailer}
          firstDayOfWeek={firstDayOfWeek}
        />
      </div>
      {links.length > 0 && (
        <>
          <div className="border-ornament border-b h-24" />
          <RetailerCard.LinksGroup
            dealerId={getDealerId(retailer)}
            dealerInfo={name}
            links={links}
          />
        </>
      )}
    </div>
  );
};
